<template>
  <layout-basic>
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
      <h3 class="title is-4">Users</h3>
      <button class="button is-primary" @click="openCreate()">Add user</button>
    </div>

    <b-table :data="users" :striped="true">
      <b-table-column field="email" label="Email" v-slot="props">
        {{ props.row.email }}
      </b-table-column>
      <b-table-column field="role" label="Role" v-slot="props">
        {{ props.row.role }}
      </b-table-column>
      <b-table-column field="actions" label="" v-slot="props" cell-class="has-text-right">
        <button class="button is-small is-info" @click="openEdit(props.row)">
          <b-icon icon="pencil-alt" size="is-small"></b-icon>
        </button>
        <button class="button is-small is-danger ml-2" @click="confirmDelete(props.row)">
          <b-icon icon="trash-alt" size="is-small"></b-icon>
        </button>
      </b-table-column>
      <template slot="empty">
        <p>No users yet</p>
      </template>
    </b-table>

    <div class="mt-4" v-if="pagination.count > pagination.limit">
      <b-pagination
        :total="pagination.count"
        :current.sync="pagination.page"
        :per-page="pagination.limit"
        order="is-centered"
        @change="onPageChange"
      />
    </div>

    <b-modal :active.sync="modal.active" has-modal-card trap-focus :width="480">
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">{{ modal.isEdit ? 'Edit user' : 'Create user' }}</p>
          <button type="button" class="delete" @click="closeModal()"/>
        </header>
        <section class="modal-card-body">
          <b-field label="Email">
            <b-input v-model="form.email" placeholder="name@example.com" type="email" required />
          </b-field>
          <b-field :label="modal.isEdit ? 'Password (leave blank to keep current)' : 'Password'">
            <b-input v-model="form.password" :placeholder="modal.isEdit ? '••••••••' : 'Enter password'" type="password" :required="!modal.isEdit"/>
          </b-field>
          <b-field label="Role">
            <b-select v-model="form.role" placeholder="Select role">
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </b-select>
          </b-field>
        </section>
        <footer class="modal-card-foot is-justify-content-flex-end">
          <button class="button" @click="closeModal()">Cancel</button>
          <button class="button is-primary" :disabled="submitting" @click="submit()">
            {{ submitting ? 'Saving...' : (modal.isEdit ? 'Save' : 'Create') }}
          </button>
        </footer>
      </div>
    </b-modal>
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/LayoutBasic'
import UserService from '@/services/UserService'
import NotificationService from '@/services/NotificationService'

export default {
  name: 'Users',
  components: { LayoutBasic },
  data () {
    return {
      users: [],
      pagination: {
        page: 1,
        limit: 20,
        count: 0
      },
      modal: {
        active: false,
        isEdit: false,
        id: null
      },
      form: {
        email: '',
        password: '',
        role: 'user'
      },
      submitting: false
    }
  },
  created () {
    this.fetchUsers()
  },
  methods: {
    fetchUsers () {
      const params = { page: this.pagination.page, pageSize: this.pagination.limit }
      UserService.list(params)
        .then(resp => {
          this.users = resp.data
          // Handle pagination headers if present
          const h = resp.headers || {}
          const toInt = (v, fallback) => {
            const n = parseInt(v, 10)
            return isNaN(n) ? fallback : n
          }
          this.pagination.count = toInt(h['pagination-count'], this.users.length)
          this.pagination.page = toInt(h['pagination-page'], this.pagination.page)
          this.pagination.limit = toInt(h['pagination-limit'], this.pagination.limit)
        })
        .catch(err => {
          console.error(err)
          NotificationService.warning((err && err.response && err.response.data && err.response.data.message) || 'Failed to load users')
        })
    },
    onPageChange (page) {
      this.pagination.page = page
      this.fetchUsers()
    },
    openCreate () {
      this.modal = { active: true, isEdit: false, id: null }
      this.form = { email: '', password: '', role: 'user' }
    },
    openEdit (user) {
      this.modal = { active: true, isEdit: true, id: user.id || user._id }
      this.form = { email: user.email, password: '', role: user.role }
    },
    closeModal () {
      this.modal.active = false
    },
    submit () {
      this.submitting = true
      const payload = { email: this.form.email, role: this.form.role }
      if (!this.modal.isEdit || (this.form.password && this.form.password.length)) {
        payload.password = this.form.password
      }

      const req = this.modal.isEdit
        ? UserService.update(this.modal.id, payload)
        : UserService.create(payload)

      req.then(() => {
        this.submitting = false
        this.modal.active = false
        NotificationService.message(this.modal.isEdit ? 'User updated' : 'User created')
        this.fetchUsers()
      }).catch(err => {
        this.submitting = false
        NotificationService.warning((err && err.response && err.response.data && err.response.data.message) || 'Operation failed')
      })
    },
    confirmDelete (user) {
      this.$buefy.dialog.confirm({
        title: 'Delete user',
        message: `Are you sure you want to delete <b>${user.email}</b>?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          const id = user.id || user._id
          UserService.delete(id).then(() => {
            NotificationService.message('User deleted')
            this.fetchUsers()
          }).catch(err => {
            NotificationService.warning((err && err.response && err.response.data && err.response.data.message) || 'Failed to delete user')
          })
        }
      })
    }
  }
}
</script>
